import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getInitialOnboardingProgress } from '../../lib/help/onboardingSteps';
import type { TabId } from '../../components/layouts/navigationConfig';

const FIRST_RUN_STORAGE_KEY = 'help_first_run_completed_v1';

export type HelpContextAlias = 'chart_view';
export type ContextId = TabId | 'global' | HelpContextAlias;

export interface HelpState {
  isHelpCenterOpen: boolean;
  firstRunCompleted: boolean;
  onboardingWelcomeDismissed: boolean;
  onboardingChecklistOpen: boolean;
  onboardingProgress: Record<string, 'pending' | 'completed'>;
  dismissedHelpHints: Record<string, boolean>;
  tourState: {
    isRunning: boolean;
    currentStepIndex: number;
    completed: boolean;
    skipped: boolean;
  };
  lastHelpContext: ContextId;
}

const readFirstRunCompleted = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(FIRST_RUN_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const persistFirstRunCompleted = (value: boolean) => {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.localStorage.setItem(FIRST_RUN_STORAGE_KEY, '1');
    else window.localStorage.removeItem(FIRST_RUN_STORAGE_KEY);
  } catch {
    // no-op
  }
};

const initialState: HelpState = {
  isHelpCenterOpen: false,
  firstRunCompleted: readFirstRunCompleted(),
  onboardingWelcomeDismissed: false,
  onboardingChecklistOpen: false,
  onboardingProgress: getInitialOnboardingProgress(),
  dismissedHelpHints: {},
  tourState: {
    isRunning: false,
    currentStepIndex: 0,
    completed: false,
    skipped: false,
  },
  lastHelpContext: 'global',
};

export const helpSlice = createSlice({
  name: 'help',
  initialState,
  reducers: {
    openHelpCenter: (state, action: PayloadAction<{ context?: ContextId } | undefined>) => {
      state.isHelpCenterOpen = true;
      if (action?.payload?.context) state.lastHelpContext = action.payload.context;
    },
    closeHelpCenter: (state) => {
      state.isHelpCenterOpen = false;
    },
    setHelpContext: (state, action: PayloadAction<ContextId>) => {
      state.lastHelpContext = action.payload;
    },
    dismissHelpHint: (state, action: PayloadAction<string>) => {
      state.dismissedHelpHints[action.payload] = true;
    },
    resetDismissedHints: (state) => {
      state.dismissedHelpHints = {};
    },
    openOnboardingChecklist: (state) => {
      state.onboardingChecklistOpen = true;
    },
    closeOnboardingChecklist: (state) => {
      state.onboardingChecklistOpen = false;
    },
    dismissOnboardingWelcome: (state) => {
      state.onboardingWelcomeDismissed = true;
    },
    completeOnboardingStep: (state, action: PayloadAction<string>) => {
      if (state.onboardingProgress[action.payload] !== undefined) {
        state.onboardingProgress[action.payload] = 'completed';
      }
    },
    resetOnboardingProgress: (state) => {
      state.onboardingProgress = getInitialOnboardingProgress();
      state.onboardingWelcomeDismissed = false;
      state.onboardingChecklistOpen = false;
      state.tourState = {
        isRunning: false,
        currentStepIndex: 0,
        completed: false,
        skipped: false,
      };
      state.firstRunCompleted = false;
      persistFirstRunCompleted(false);
    },
    startOnboardingTour: (state) => {
      state.tourState.isRunning = true;
      state.tourState.currentStepIndex = 0;
      state.tourState.completed = false;
      state.tourState.skipped = false;
      state.onboardingChecklistOpen = true;
    },
    setOnboardingTourStep: (state, action: PayloadAction<number>) => {
      state.tourState.currentStepIndex = action.payload;
    },
    skipOnboardingTour: (state) => {
      state.tourState.isRunning = false;
      state.tourState.skipped = true;
      state.tourState.completed = false;
      state.onboardingChecklistOpen = false;
    },
    completeOnboardingTour: (state) => {
      state.tourState.isRunning = false;
      state.tourState.completed = true;
      state.tourState.skipped = false;
      Object.keys(state.onboardingProgress).forEach((stepId) => {
        state.onboardingProgress[stepId] = 'completed';
      });
      state.onboardingChecklistOpen = false;
      state.firstRunCompleted = true;
      persistFirstRunCompleted(true);
    },
    markFirstRunCompleted: (state) => {
      state.firstRunCompleted = true;
      persistFirstRunCompleted(true);
    },
  },
});

export const {
  openHelpCenter,
  closeHelpCenter,
  setHelpContext,
  dismissHelpHint,
  resetDismissedHints,
  openOnboardingChecklist,
  closeOnboardingChecklist,
  dismissOnboardingWelcome,
  completeOnboardingStep,
  resetOnboardingProgress,
  startOnboardingTour,
  setOnboardingTourStep,
  skipOnboardingTour,
  completeOnboardingTour,
  markFirstRunCompleted,
} = helpSlice.actions;

export default helpSlice.reducer;
